/**
 * useGuardTrail Hook
 *
 * Fetches and manages location history for guard trail visualization.
 * Uses react-query for caching and automatic refetching.
 *
 * @see Issue 7.2: Add location trail visualization
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getGuardLocationHistory } from '../../../../../api/canguard/getGuardLocations';
import { LocationHistoryPoint } from './types';

/** Options for the useGuardTrail hook */
export interface UseGuardTrailOptions {
  /** Whether trail fetching is enabled (default: false - only fetch when visible) */
  enabled?: boolean;
  /** How far back to fetch history (ISO date string, default: 4 hours ago) */
  since?: string;
  /** Maximum number of points to fetch (default: 100) */
  limit?: number;
  /** Refetch interval in milliseconds (default: 60000 = 1 minute) */
  refetchInterval?: number;
}

/** Return value from useGuardTrail hook */
export interface UseGuardTrailResult {
  /** Array of location points forming the trail path */
  trail: LocationHistoryPoint[];
  /** Whether the trail is currently loading */
  isLoading: boolean;
  /** Whether the trail is being refetched */
  isFetching: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Total count of available points (may be more than returned if limited) */
  totalCount: number;
  /** Manually refetch the trail */
  refetch: () => void;
}

/**
 * Calculate default "since" time (4 hours ago)
 */
function getDefaultSince(): string {
  const fourHoursAgo = new Date();
  fourHoursAgo.setHours(fourHoursAgo.getHours() - 4);
  return fourHoursAgo.toISOString();
}

/**
 * Hook for fetching guard location history for trail visualization.
 *
 * @param guardId - The guard's ID to fetch trail for
 * @param options - Configuration options
 * @returns Trail data and loading state
 *
 * @example
 * ```tsx
 * const { trail, isLoading } = useGuardTrail(guardId, {
 *   enabled: isTrailVisible,
 *   since: patrolStartTime,
 * });
 * ```
 */
export function useGuardTrail(
  guardId: number | null,
  options: UseGuardTrailOptions = {}
): UseGuardTrailResult {
  const {
    enabled = false,
    since,
    limit = 100,
    refetchInterval = 60000, // 1 minute
  } = options;

  // Stabilize the default "since" value so it doesn't change every render
  // (changing queryKey every render would cause an infinite fetch loop)
  const stableSince = useMemo(() => since ?? getDefaultSince(), [since]);

  const query = useQuery({
    queryKey: ['guardTrail', guardId, stableSince, limit],
    queryFn: async () => {
      if (!guardId) {
        return { contactId: 0, locations: [], count: 0 };
      }
      return getGuardLocationHistory(guardId, {
        from: stableSince,
        limit,
      });
    },
    enabled: enabled && guardId !== null,
    refetchInterval: enabled ? refetchInterval : false,
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  return {
    // Return locations in chronological order (oldest first) for path drawing
    trail: query.data?.locations
      ? [...query.data.locations].reverse()
      : [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    totalCount: query.data?.count ?? 0,
    refetch: query.refetch,
  };
}

export default useGuardTrail;
